import api from './api';

export interface PaymentMethodDTO {
  id: number;
  mpCardToken: string;
  mpCustomerId: string;
  cardLastFour: string;
  cardBrand: string;
  cardHolderName: string;
  expirationMonth: number;
  expirationYear: number;
  isDefault: boolean;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  deactivatedAt?: string;
  mpMetadata?: string;
}

class PaymentMethodService {
  private base = '/api/payment-methods';

  async getUserPaymentMethods(): Promise<PaymentMethodDTO[]> {
    const { data } = await api.get<PaymentMethodDTO[]>(`${this.base}`);
    return data;
  }

  async getDefaultPaymentMethod(): Promise<PaymentMethodDTO | null> {
    try {
      const { data } = await api.get<PaymentMethodDTO>(`${this.base}/default`);
      return data;
    } catch (error: any) {
      if (error?.response?.status === 404) {
        return null;
      }
      throw error;
    }
  }

  async setAsDefault(paymentMethodId: number): Promise<void> {
    await api.patch(`${this.base}/${paymentMethodId}/set-default`);
  }

  async deactivatePaymentMethod(paymentMethodId: number): Promise<void> {
    await api.patch(`${this.base}/${paymentMethodId}/deactivate`);
  }

  async reactivatePaymentMethod(paymentMethodId: number): Promise<void> {
    await api.patch(`${this.base}/${paymentMethodId}/reactivate`);
  }

  async getExpiringPaymentMethods(monthsAhead: number = 2): Promise<PaymentMethodDTO[]> {
    const { data } = await api.get<PaymentMethodDTO[]>(`${this.base}/expiring`, {
      params: { monthsAhead }
    });
    return data;
  }

  async checkCardExists(lastFour: string, brand: string): Promise<boolean> {
    const { data } = await api.get<{ exists: boolean }>(`${this.base}/check-exists`, {
      params: { lastFour, brand }
    });
    return data.exists;
  }

  async createPaymentMethod(paymentMethodData: CreatePaymentMethodRequest): Promise<PaymentMethodDTO> {
    const { data } = await api.post<any>(`${this.base}`, paymentMethodData);
    return data.paymentMethod;
  }
}

export interface CreatePaymentMethodRequest {
  cardToken: string;
  cardBrand: string;
  cardLastFour: string;
  cardHolderName: string;
  expirationMonth: number;
  expirationYear: number;
  setAsDefault?: boolean;
}

export const paymentMethodService = new PaymentMethodService();
export default paymentMethodService;